import { useCallback, useEffect, useRef, useState } from "react";

import { ISelectOption } from "src/core/constants/options";
import { Roles } from "src/core/constants/roles";
import { useGetUsersLazyQuery } from "src/core/gql/graphql";

/** Nombre minimum de caractères (après trim) avant d'interroger `searchUsers`. */
export const USER_SEARCH_MIN_QUERY_LEN = 1;
export const USER_SEARCH_DEBOUNCE_MS = 200;
export const USER_SEARCH_LIMIT = 50;

const DEFAULT_ALLOWED_GROUPS = [Roles.EXPERT_COMPTABLE, Roles.ASSOCIE, Roles.ADMINISTRATEUR];
const EMPTY_OPTIONS: ISelectOption[] = [];

/** Compte technique utilisé pour l'import initial des données clients. */
const IMPORT_USER_FIRST_NAME = "IMPORT";
const IMPORT_USER_LAST_NAME = "USER";

function isTechnicalImportUser(user: {
  firstName?: string | null;
  lastName?: string | null;
}): boolean {
  return (
    user.firstName?.trim().toUpperCase() === IMPORT_USER_FIRST_NAME &&
    user.lastName?.trim().toUpperCase() === IMPORT_USER_LAST_NAME
  );
}

function toOption(user: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
}): ISelectOption {
  return {
    value: user.id,
    label: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  };
}

function mergeOptions(current: ISelectOption[], incoming: ISelectOption[]): ISelectOption[] {
  const map = new Map(current.map((option) => [option.value, option]));
  let changed = false;

  for (const option of incoming) {
    if (!option.value || !option.label) continue;
    const existing = map.get(option.value);
    if (!existing || existing.label !== option.label) {
      map.set(option.value, option);
      changed = true;
    }
  }

  return changed ? [...map.values()] : current;
}

/**
 * Recherche d'utilisateurs pour un autocomplete.
 * Aucune requête au montage : la recherche part après {@link USER_SEARCH_MIN_QUERY_LEN}
 * caractères et {@link USER_SEARCH_DEBOUNCE_MS} ms.
 */
export function useOwnerOptions(
  allowedGroups: string[] = DEFAULT_ALLOWED_GROUPS,
  pinnedOptions: ISelectOption[] = EMPTY_OPTIONS
): {
  options: ISelectOption[];
  loading: boolean;
  error?: string;
  search: string;
  setSearch: (value: string) => void;
  rememberOption: (option: ISelectOption) => void;
  labelFor: (id: string) => string | undefined;
} {
  const [search, setSearchValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [results, setResults] = useState<ISelectOption[]>([]);
  const [remembered, setRemembered] = useState<ISelectOption[]>(pinnedOptions);
  const [requestLoading, setRequestLoading] = useState(false);

  const groupsKey = allowedGroups.join("\u0000");
  const pinnedKey = pinnedOptions
    .map((option) => `${option.value}\u0000${option.label}`)
    .join("\u0001");

  const allowedGroupsRef = useRef(allowedGroups);
  allowedGroupsRef.current = allowedGroups;
  const pinnedOptionsRef = useRef(pinnedOptions);
  pinnedOptionsRef.current = pinnedOptions;

  const debounceRef = useRef<number | null>(null);
  const requestIdRef = useRef(0);

  const [searchUsers, { error }] = useGetUsersLazyQuery({
    fetchPolicy: "network-only",
  });
  const searchUsersRef = useRef(searchUsers);
  searchUsersRef.current = searchUsers;

  const clearDebounce = useCallback(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    setRemembered((prev) => mergeOptions(prev, pinnedOptionsRef.current));
  }, [pinnedKey]);

  useEffect(() => clearDebounce, [clearDebounce]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      clearDebounce();

      const trimmed = value.trim();
      if (trimmed.length < USER_SEARCH_MIN_QUERY_LEN) {
        requestIdRef.current += 1;
        setIsDebouncing(false);
        setDebouncedQuery("");
        setResults([]);
        setRequestLoading(false);
        return;
      }

      setIsDebouncing(true);
      debounceRef.current = window.setTimeout(() => {
        setDebouncedQuery(trimmed);
        setIsDebouncing(false);
      }, USER_SEARCH_DEBOUNCE_MS);
    },
    [clearDebounce]
  );

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < USER_SEARCH_MIN_QUERY_LEN) return;

    const requestId = ++requestIdRef.current;
    setRequestLoading(true);

    searchUsersRef
      .current({
        variables: {
          string: trimmed,
          allowedGroups: allowedGroupsRef.current,
          limit: USER_SEARCH_LIMIT,
        },
      })
      .then((response) => {
        if (requestIdRef.current !== requestId) return;
        const users = response.data?.searchUsers ?? [];
        setResults(
          users.filter((user) => !isTechnicalImportUser(user)).map((user) => toOption(user))
        );
      })
      .catch(() => {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setRequestLoading(false);
        }
      });
  }, [debouncedQuery, groupsKey]);

  const rememberOption = useCallback((option: ISelectOption) => {
    if (!option.value || !option.label) return;
    setRemembered((prev) => mergeOptions(prev, [option]));
  }, []);

  const labelFor = useCallback(
    (id: string) => {
      if (!id || id === "none") return undefined;
      return (
        results.find((option) => option.value === id)?.label ??
        remembered.find((option) => option.value === id)?.label
      );
    },
    [results, remembered]
  );

  return {
    options: results,
    loading: isDebouncing || requestLoading,
    error: error instanceof Error ? error.message : undefined,
    search,
    setSearch,
    rememberOption,
    labelFor,
  };
}
